import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useToast } from "@/components/providers/ToastProvider";
import { useAppSelector } from "@/src/redux/hooks";
import {
  mapListItemToContextItem,
  mapPropertyDtosToListItems,
  normalizePropertyListData,
} from "@/src/services/propertiesMapper";
import { propertiesService } from "@/src/services/propertiesService";
import type { PropertyContextItem } from "@/src/types/property.types";
import { getApiErrorMessage } from "@/src/utils/helpers";

/** Marketing stats on login when the user is not authenticated. */
export const DEMO_PROPERTY_STATS = {
  count: 3,
  avgHealthScore: 86,
  totalAssets: 244,
};

const EMPTY_ACTIVE: PropertyContextItem = {
  id: "",
  name: "No property",
  address: "",
  rooms: 0,
  lotSize: "—",
  assets: 0,
  healthScore: null,
  isActive: false,
  alerts: 0,
};

interface PropertyContextValue {
  active: PropertyContextItem;
  properties: PropertyContextItem[];
  loading: boolean;
  isActivating: boolean;
  setActive: (id: string) => Promise<void>;
  refreshProperties: () => Promise<void>;
}

const PropertyCtx = createContext<PropertyContextValue>({
  active: EMPTY_ACTIVE,
  properties: [],
  loading: false,
  isActivating: false,
  setActive: async () => {},
  refreshProperties: async () => {},
});

export function PropertyProvider({ children }: { children: React.ReactNode }) {
  const { showToast } = useToast();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [properties, setProperties] = useState<PropertyContextItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [isActivating, setIsActivating] = useState(false);

  const refreshProperties = useCallback(async () => {
    if (!isAuthenticated) {
      setProperties([]);
      setActiveId("");
      return;
    }

    setLoading(true);
    try {
      const data = await propertiesService.list();
      const items = mapPropertyDtosToListItems(normalizePropertyListData(data)).map(
        mapListItemToContextItem,
      );
      setProperties(items);

      const activeFromApi = items.find((item) => item.isActive);
      const stored =
        typeof window !== "undefined" ? localStorage.getItem("envella-property") : null;
      const storedItem = stored ? items.find((item) => item.id === stored) : undefined;

      if (activeFromApi) {
        setActiveId(activeFromApi.id);
        if (typeof window !== "undefined") {
          localStorage.setItem("envella-property", activeFromApi.id);
        }
      } else if (storedItem) {
        setActiveId(storedItem.id);
      } else if (items[0]) {
        setActiveId(items[0].id);
      } else {
        setActiveId("");
      }
    } catch (error) {
      setProperties([]);
      setActiveId("");
      showToast(getApiErrorMessage(error, "Could not load properties."), "error");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, showToast]);

  useEffect(() => {
    void refreshProperties();
  }, [refreshProperties]);

  const setActive = useCallback(
    async (id: string) => {
      const target = properties.find((property) => property.id === id);
      if (!target || isActivating) {
        return;
      }

      if (target.isActive) {
        setActiveId(id);
        if (typeof window !== "undefined") {
          localStorage.setItem("envella-property", id);
        }
        return;
      }

      setIsActivating(true);
      try {
        await propertiesService.activate(id);
        if (typeof window !== "undefined") {
          localStorage.setItem("envella-property", id);
        }
        setActiveId(id);
        setProperties((prev) =>
          prev.map((property) => ({
            ...property,
            isActive: property.id === id,
          })),
        );
      } catch (error) {
        showToast(getApiErrorMessage(error, "Could not set active property."), "error");
      } finally {
        setIsActivating(false);
      }
    },
    [isActivating, properties, showToast],
  );

  const active = useMemo(() => {
    if (!properties.length) {
      return EMPTY_ACTIVE;
    }
    const activeFromList = properties.find((property) => property.isActive);
    if (activeFromList) {
      return activeFromList;
    }
    return properties.find((property) => property.id === activeId) ?? properties[0];
  }, [activeId, properties]);

  const value = useMemo(
    () => ({
      active,
      properties,
      loading,
      isActivating,
      setActive,
      refreshProperties,
    }),
    [active, properties, loading, isActivating, setActive, refreshProperties],
  );

  return <PropertyCtx.Provider value={value}>{children}</PropertyCtx.Provider>;
}

export function useProperty() {
  return useContext(PropertyCtx);
}
